import { Grid } from "../core/Grid.ts";
import { CellType } from "../core/types.ts";
import { canvasFont } from "./canvasType.ts";
import { CELL_SIZE } from "../core/config.ts";
import {
  THREAT_PULSE_DURATION,
  COLORS as CORE_COLORS,
} from "../core/constants.ts";
import { getEpochColors } from "./EpochTheme.ts";
import { mixHex } from "./CorruptionTheme.ts";
import { arenaMotionScale } from "../engine/MotionSettings.ts";
import { BOARD, ECHELON } from "./designTokens.ts";
import { MainframeRenderer } from "./MainframeRenderer.ts";
import {
  GridAmbience,
  SECTOR_STEP as AMBIENCE_SECTOR_STEP,
} from "./GridAmbience.ts";
import type { BossPathColor } from "./BossPathColor.ts";

/** BL-286 — the board-surface ink. ONE colour for the whole lattice: the
 *  hierarchy is carried by alpha alone, so a sector line is unmistakably the
 *  same material as a hairline, just lit harder. Brighter than the old flat
 *  hairline ink on purpose — that headroom is what gives depth and per-line
 *  variation somewhere to live. */
const GRID_INK = BOARD.line;
/**
 * Laydown alphas, calibrated against the pre-BL-286 board (a uniform G≈33 line
 * over the G=8 void): a hairline lands near G≈28 and a sector line near G≈44.
 * The mean is a touch below where it was, so the surface gained structure
 * WITHOUT gaining presence — the grid must never climb the §3 hierarchy.
 */
const GRID_MINOR_ALPHA = 0.35;
const GRID_SECTOR_ALPHA = 0.43;
/** The soft 3 px bed under a sector line — its glow bleed. */
const GRID_BLEED_ALPHA = 0.07;
/** How far the far edge of the board falls off (0 = flat). Kept gentle: this
 *  compounds with the along-length gradient AND the BL-235 board vignette. */
const GRID_DEPTH_FALLOFF = 0.22;
/**
 * Where the board's falloff is brightest, as a fraction of the board offset from
 * dead centre toward the **upper left**.
 *
 * This is the one place the surface states a light direction, and it is not a
 * free choice: `EmissiveMaterial` offsets every body's hot core to
 * `(cx − 0.34r, cy − 0.34r)` — up and to the left — which makes upper-left the
 * only key light the renderer actually declares. A centre-symmetric falloff is a
 * vignette, not depth, because the ground then claims the light comes from
 * everywhere while every body on it says otherwise; that mismatch is why lit
 * bodies read as stickers on a flat plane rather than as objects in a space.
 *
 * Deliberately small (0.10, not a full diagonal gradient): the *character* of
 * the surface must not change — the centre-weighted read that serves gameplay
 * focus stays, the brightest point simply moves to where the light is. The
 * rejected alternative was a directional key from the **Core**, which sits at
 * (936, 360) — dead right of centre, **138° away** from the body key. That would
 * have lit the ground from the opposite side to every object standing on it.
 */
const GRID_LIGHT_BIAS = 0.1;
/** Every Nth line reads as a sector boundary. Shared with the live layer so
 *  the ambience runs on exactly the lattice the board draws. */
const SECTOR_STEP = AMBIENCE_SECTOR_STEP;

/** Deterministic 0..1 hash — per-line brightness variation without an RNG, so
 *  the baked cache is identical every run. */
function hash01(n: number): number {
  const s = Math.sin(n * 12.9898) * 43758.5453;
  return s - Math.floor(s);
}

const COLORS = {
  gridLine: "#0a2a1a",
  gridNodeGlow: `${CORE_COLORS.SENTINEL_GREEN}20`,
  tileEmpty: "#080810",
  tileTower: "#0f1a14",
  tileSpawn: "#001a0f",
  tileExit: "#1a0505",
  spawnIcon: CORE_COLORS.SENTINEL_GREEN,
  exitIcon: "#ff3333",
  dormantSpawn: CORE_COLORS.AMBER_WARN,
};

/** BL-232 — duration (ms) of the per-wave "GO" data-flow flare. */
const WAVE_SURGE_MS = 600;

export class GridRenderer {
  private grid: Grid;
  private ctx: CanvasRenderingContext2D;
  private size: number;
  private wave = 0;
  private gridLinesCache: HTMLCanvasElement | null = null;
  private gridLinesCacheW = -1;
  private gridLinesCacheH = -1;
  private tileCache: HTMLCanvasElement | null = null;
  private tileCacheDirty = true;
  private specialTiles: Array<{ x: number; y: number; type: number }> | null =
    null;
  private getHiddenSpawnPositions:
    | (() => Array<{ x: number; y: number }>)
    | null = null;
  private pathCells: Array<{ x: number; y: number }> = [];
  private pathCellsDirty = true;
  readonly mainframe = new MainframeRenderer();
  /** BL-286 — the live board under-layer (see {@link GridAmbience}). */
  private ambience_: GridAmbience | null = null;
  private rejectFlash: {
    col: number;
    row: number;
    timer: number;
    color: string;
  } | null = null;
  private threatPulseUntil_ = 0;
  /** BL-232 — deadline (Date.now() ms) for the per-wave "GO" data-flow flare. */
  private waveSurgeUntil_ = 0;
  /** S126 — current corruption intensity (0 outside Acts II/III). */
  private corruption = 0;
  bossPathColor?: BossPathColor;

  constructor(grid: Grid, ctx: CanvasRenderingContext2D, cellSize = CELL_SIZE) {
    this.grid = grid;
    this.ctx = ctx;
    this.size = cellSize;
  }

  setWave(wave: number): void {
    this.wave = wave;
  }

  /** S126: the frame's computed corruption intensity (0 in Act I — the
   *  `mixHex(_, _, 0) === base` contract keeps Act I byte-identical). Pushed
   *  by the render pipeline; consumed by the portal/data-flow tint + the
   *  Mainframe taint. Read-only theming — never sim state. */
  setCorruptionIntensity(intensity: number): void {
    this.corruption = intensity;
  }

  setHiddenSpawnPositionSource(
    getter: () => Array<{ x: number; y: number }>,
  ): void {
    this.getHiddenSpawnPositions = getter;
  }

  /**
   * Drop the caches that depend on CELL CONTENT — tiles, path cells, special
   * tiles. Called whenever a cell changes (tower placed or sold) and on the
   * three reveal events.
   *
   * BL-293 — it deliberately no longer dirties the LATTICE.
   * `renderGridLinesToContext` takes `(ctx, widthPx, heightPx)` and reads no
   * cell state whatsoever: the lattice is a function of the board's dimensions,
   * and those never change at runtime. Dirtying it here meant every single
   * tower placement re-baked the entire board surface — and after BL-286 that
   * surface got its sector hierarchy, hashed per-line variation and directional
   * falloff, i.e. it became the most expensive bake in the renderer. The cost
   * landed on the exact frame the player taps to place a tower.
   * The lattice is now rebaked only when its inputs actually change (see
   * {@link drawGridLines}, which compares the baked dimensions), so the
   * placement frame stops paying for a surface that cannot have moved.
   */
  invalidateCellCaches(): void {
    this.tileCacheDirty = true;
    this.pathCellsDirty = true;
    this.specialTiles = null;
  }

  render(): void {
    const now = performance.now();
    this.drawTiles();
    this.drawGridLines();
    // BL-286 — the live board layer sits directly on the baked surface and
    // UNDER everything with meaning (path, portals, blockers, and every entity
    // the pipeline draws after us), so the visual hierarchy holds by draw order
    // rather than by tuning.
    this.ambience().render(this.ctx, now, this.gridLinesCache);
    this.drawDataFlow();
    this.drawSpecialTiles();
    this.drawBlockers(now);
  }

  /** Lazily built so the ambience always matches the live board dimensions
   *  (and costs nothing in a headless test that never renders). */
  private ambience(): GridAmbience {
    if (!this.ambience_) {
      this.ambience_ = new GridAmbience(
        this.grid.width * this.size,
        this.grid.height * this.size,
        this.size,
      );
    }
    return this.ambience_;
  }

  private drawTiles(): void {
    const widthPx = this.grid.width * this.size;
    const heightPx = this.grid.height * this.size;

    if (this.tileCacheDirty || !this.tileCache) {
      if (!this.tileCache) {
        this.tileCache = document.createElement("canvas");
      }
      this.tileCache.width = widthPx;
      this.tileCache.height = heightPx;
      const offCtx = this.tileCache.getContext("2d")!;
      const size = this.size;
      for (let row = 0; row < this.grid.height; row++) {
        for (let col = 0; col < this.grid.width; col++) {
          const cell = this.grid.getCell(col, row);
          offCtx.fillStyle = this.getTileColor(cell?.type ?? CellType.EMPTY);
          offCtx.fillRect(col * size, row * size, size, size);
        }
      }
      this.tileCacheDirty = false;
    }

    this.ctx.drawImage(this.tileCache, 0, 0);
  }

  private drawGridLines(): void {
    const widthPx = this.grid.width * this.size;
    const heightPx = this.grid.height * this.size;

    // BL-293 — rebake only when the lattice's ACTUAL inputs changed. Its only
    // inputs are the two dimensions, so comparing them is the complete test;
    // a cell change can no longer reach this surface.
    if (
      !this.gridLinesCache ||
      this.gridLinesCacheW !== widthPx ||
      this.gridLinesCacheH !== heightPx
    ) {
      if (!this.gridLinesCache) {
        this.gridLinesCache = document.createElement("canvas");
      }
      this.gridLinesCache.width = widthPx;
      this.gridLinesCache.height = heightPx;
      const offCtx = this.gridLinesCache.getContext("2d")!;
      this.renderGridLinesToContext(offCtx, widthPx, heightPx);
      this.gridLinesCacheW = widthPx;
      this.gridLinesCacheH = heightPx;
    }

    this.ctx.drawImage(this.gridLinesCache, 0, 0);
  }

  /**
   * BL-286 — the board surface, baked once into the line cache.
   *
   * What was here drew every line at one flat colour and dropped a dot on every
   * third crossing: a debug grid, and the largest surface on screen was the one
   * thing on the board with no design in it. What replaces it is the same
   * lattice with a MATERIAL:
   *
   *   - a sector hierarchy — every {@link SECTOR_STEP}th line reads as a
   *     boundary (a crisp core over a soft 3 px bleed bed, the ART §6.9
   *     "glowing, SDF-edged line"), the rest stay hairlines;
   *   - depth on two axes — each line dims along its own length toward the
   *     board edge (one gradient per axis, shared by every line) and dims again
   *     the further out it sits, so the surface falls away instead of lying
   *     flat;
   *   - per-line brightness variation from a deterministic hash, which is what
   *     actually kills the "CSS grid" read: real panels are never uniform;
   *   - junction chrome — sector crossings get a node plus, on a sparse hashed
   *     subset, registration ticks. Micro-detail that rewards a close look and
   *     disappears at a glance.
   *
   * Every bit of it lands in the cached canvas, so the per-frame cost is the
   * same single `drawImage` it has always been — nothing here is paid twice.
   */
  private renderGridLinesToContext(
    ctx: CanvasRenderingContext2D,
    widthPx: number,
    heightPx: number,
  ): void {
    const size = this.size;
    // The falloff origin — the board's lit point — sits off-centre toward the
    // upper left, on the same key the bodies are lit from (GRID_LIGHT_BIAS).
    const midX = widthPx * (0.5 - GRID_LIGHT_BIAS);
    const midY = heightPx * (0.5 - GRID_LIGHT_BIAS);
    // Normalise against the FAR side of the off-centre origin, so the darkest
    // corner lands at exactly GRID_DEPTH_FALLOFF and the amplitude is unchanged.
    const spanX = Math.max(midX, widthPx - midX);
    const spanY = Math.max(midY, heightPx - midY);

    // One gradient per axis, reused by every line on it: full strength through
    // the middle of the board, falling off toward both ends.
    const vGrad = ctx.createLinearGradient(0, 0, 0, heightPx);
    const hGrad = ctx.createLinearGradient(0, 0, widthPx, 0);
    for (const g of [vGrad, hGrad]) {
      g.addColorStop(0, `${GRID_INK}AD`);
      g.addColorStop(0.3, GRID_INK);
      g.addColorStop(0.7, GRID_INK);
      g.addColorStop(1, `${GRID_INK}AD`);
    }

    ctx.lineWidth = 1;

    for (let col = 0; col <= this.grid.width; col++) {
      const x = col * size + 0.5;
      const sector = col % SECTOR_STEP === 0;
      // Depth: lines far from the centre sit further "back".
      const depth = 1 - GRID_DEPTH_FALLOFF * (Math.abs(x - midX) / spanX);
      // Sector lines are the STRUCTURE, so they hold a near-constant weight;
      // hairlines are the TEXTURE and vary widely. Letting both wander equally
      // was what blurred the hierarchy back into noise on the first pass.
      const vary = sector
        ? 0.94 + hash01(col * 4.13) * 0.12
        : 0.82 + hash01(col * 4.13) * 0.36;
      if (sector) {
        ctx.strokeStyle = GRID_INK;
        ctx.lineWidth = 3;
        ctx.globalAlpha = GRID_BLEED_ALPHA * depth;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, heightPx);
        ctx.stroke();
        ctx.lineWidth = 1;
      }
      ctx.strokeStyle = sector ? GRID_INK : vGrad;
      ctx.globalAlpha =
        (sector ? GRID_SECTOR_ALPHA : GRID_MINOR_ALPHA) * depth * vary;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, heightPx);
      ctx.stroke();
    }

    for (let row = 0; row <= this.grid.height; row++) {
      const y = row * size + 0.5;
      const sector = row % SECTOR_STEP === 0;
      const depth = 1 - GRID_DEPTH_FALLOFF * (Math.abs(y - midY) / spanY);
      const vary = sector
        ? 0.94 + hash01(row * 7.31 + 31) * 0.12
        : 0.82 + hash01(row * 7.31 + 31) * 0.36;
      if (sector) {
        ctx.strokeStyle = GRID_INK;
        ctx.lineWidth = 3;
        ctx.globalAlpha = GRID_BLEED_ALPHA * depth;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(widthPx, y);
        ctx.stroke();
        ctx.lineWidth = 1;
      }
      ctx.strokeStyle = sector ? GRID_INK : hGrad;
      ctx.globalAlpha =
        (sector ? GRID_SECTOR_ALPHA : GRID_MINOR_ALPHA) * depth * vary;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(widthPx, y);
      ctx.stroke();
    }

    // Circuit-board nodes. Minor crossings keep the original every-third dot
    // (now with hashed brightness); sector crossings get the junction chrome.
    for (let row = 0; row <= this.grid.height; row++) {
      for (let col = 0; col <= this.grid.width; col++) {
        const x = col * size;
        const y = row * size;
        const depth =
          (1 - GRID_DEPTH_FALLOFF * (Math.abs(x - midX) / spanX)) *
          (1 - GRID_DEPTH_FALLOFF * (Math.abs(y - midY) / spanY));
        const junction = col % SECTOR_STEP === 0 && row % SECTOR_STEP === 0;

        if (junction) {
          ctx.fillStyle = GRID_INK;
          ctx.globalAlpha = 0.5 * depth;
          ctx.beginPath();
          ctx.arc(x, y, 1.6, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 0.14 * depth;
          ctx.beginPath();
          ctx.arc(x, y, 3.4, 0, Math.PI * 2);
          ctx.fill();

          // Registration ticks on a sparse hashed subset — technical
          // micro-detail, invisible unless you go looking for it.
          if (hash01(col * 2.9 + row * 5.3) > 0.62) {
            ctx.strokeStyle = GRID_INK;
            ctx.globalAlpha = 0.3 * depth;
            ctx.beginPath();
            ctx.moveTo(x - 5.5, y - 3.5);
            ctx.lineTo(x - 3.5, y - 3.5);
            ctx.lineTo(x - 3.5, y - 5.5);
            ctx.moveTo(x + 5.5, y + 3.5);
            ctx.lineTo(x + 3.5, y + 3.5);
            ctx.lineTo(x + 3.5, y + 5.5);
            ctx.stroke();
          }
        } else if ((row + col) % 3 === 0) {
          ctx.fillStyle = COLORS.gridNodeGlow;
          ctx.globalAlpha = (0.55 + hash01(col * 1.7 + row * 3.1) * 0.45) * depth;
          ctx.beginPath();
          ctx.arc(x, y, 1.3, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    ctx.globalAlpha = 1;
  }

  private drawSpecialTiles(): void {
    const ctx = this.ctx;
    const size = this.size;
    const epoch = getEpochColors(this.wave);
    const time = performance.now();

    // Spawn points: swirling vortex portal. S126: the gates are HER ingress —
    // the ring lerps toward ECHELON's signal with the corruption (exact base
    // color at intensity 0, so Act I stays byte-identical).
    const portalColor = mixHex(
      epoch.primary,
      ECHELON.SIGNAL,
      0.5 * this.corruption,
    );
    for (const { x: col, y: row } of this.grid.getEntries()) {
      const cx = col * size + size / 2;
      const cy = row * size + size / 2;
      this.drawSpawnPortal(ctx, cx, cy, size, portalColor, time);
    }

    // Exit points: Mainframe sprite (replaces old red X)
    for (const { x: col, y: row } of this.grid.getExits()) {
      this.mainframe.render(ctx, col, row, this.wave, this.corruption);
    }

    // CR-26: Dormant spawn nodes — sealed glyphs (M5-F12: cached positions)
    if (!this.specialTiles) {
      this.specialTiles = [];
      for (let row = 0; row < this.grid.height; row++) {
        for (let col = 0; col < this.grid.width; col++) {
          const cell = this.grid.getCell(col, row);
          if (
            cell?.type === CellType.DORMANT_SPAWN ||
            cell?.type === CellType.HIDDEN_SPAWN
          ) {
            this.specialTiles.push({ x: col, y: row, type: cell.type });
          }
        }
      }
    }
    const renderedHidden = new Set<string>();
    for (const tile of this.specialTiles) {
      // CR-H4 (Bug 2): Safety net — re-validate live cell type before drawing.
      // Defends against any failure of the cache-invalidation chain
      // (Grid.setOnCellChanged → invalidateCellCaches) by checking the
      // current grid state per-frame. If a cell was transitioned out of
      // DORMANT_SPAWN/HIDDEN_SPAWN (e.g., breach activated it to PATH_ENTRY),
      // skip drawing — no more orphaned padlock icons.
      const live = this.grid.getCell(tile.x, tile.y);
      if (
        !live ||
        (live.type !== CellType.DORMANT_SPAWN &&
          live.type !== CellType.HIDDEN_SPAWN)
      ) {
        continue;
      }
      if (live.type === CellType.DORMANT_SPAWN) {
        this.drawDormantSpawn(
          ctx,
          tile.x * size + size / 2,
          tile.y * size + size / 2,
          size,
          time,
        );
      } else {
        renderedHidden.add(`${tile.x},${tile.y}`);
        this.drawHiddenSpawnPing(
          ctx,
          tile.x * size + size / 2,
          tile.y * size + size / 2,
          size,
          time,
        );
      }
    }

    for (const pos of this.getHiddenSpawnPositions?.() ?? []) {
      const key = `${pos.x},${pos.y}`;
      if (renderedHidden.has(key)) continue;
      this.drawHiddenSpawnPing(
        ctx,
        pos.x * size + size / 2,
        pos.y * size + size / 2,
        size,
        time,
      );
    }
  }

  private drawDormantSpawn(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    size: number,
    time: number,
  ): void {
    const r = size * 0.3;
    const pulse = 0.3 + Math.sin(time / 1500) * 0.1;

    // Dimmed sealed circle
    ctx.strokeStyle = COLORS.dormantSpawn;
    ctx.lineWidth = 1;
    ctx.globalAlpha = pulse;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();

    // Padlock icon (simple)
    ctx.fillStyle = COLORS.dormantSpawn;
    ctx.globalAlpha = pulse * 0.8;
    ctx.fillRect(cx - 3, cy - 1, 6, 5);
    ctx.strokeStyle = COLORS.dormantSpawn;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(cx, cy - 2, 3, Math.PI, 0);
    ctx.stroke();
    ctx.globalAlpha = 1;

    // BL-125 (S137) — no "SEALED" label. It read as cover/closure to the
    // operator, while a DORMANT_SPAWN cell is permeable by design (a future
    // enemy origin, not a blocker). The amber padlock+circle still mark the
    // cell as a spawn marker; the red BLOCKER's hex-noise+dashed border
    // remains the only "this protects enemies" affordance.

    // Threat pulse ring (Phase 2: expanding red ring on wave 1 start)
    const now = Date.now();
    if (now < this.threatPulseUntil_) {
      const progress =
        1 - (this.threatPulseUntil_ - now) / THREAT_PULSE_DURATION;
      const pulseR = r * 0.5 + r * 1.5 * progress;
      ctx.beginPath();
      ctx.arc(cx, cy, pulseR, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(255, 45, 85, ${1 - progress})`;
      ctx.lineWidth = 2;
      ctx.globalAlpha = 1;
      ctx.stroke();
    }
  }

  /** Hidden spawn: faint periodic sonar-like radar ping (no icon, no path) */
  private drawHiddenSpawnPing(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    size: number,
    time: number,
  ): void {
    // Ping every 3 seconds, expand over 1.5s then fade
    const cycle = 3000;
    const phase = time % cycle;
    if (phase > 1500) return; // invisible between pings

    const t = phase / 1500; // 0..1 over the ping duration
    const r = size * 0.1 + size * 0.4 * t;
    const alpha = 0.15 * (1 - t);

    ctx.strokeStyle = COLORS.gridNodeGlow;
    ctx.lineWidth = 1;
    ctx.globalAlpha = alpha;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();

    // Tiny center dot at ping peak
    if (t < 0.3) {
      ctx.fillStyle = COLORS.gridNodeGlow;
      ctx.globalAlpha = 0.1 * (1 - t / 0.3);
      ctx.beginPath();
      ctx.arc(cx, cy, 1.5, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.globalAlpha = 1;
  }

  private drawSpawnPortal(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    size: number,
    color: string,
    time: number,
  ): void {
    const r = size * 0.35;
    const rotation = (time / 2000) * Math.PI * 2;

    // Outer rotating ring
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.globalAlpha = 0.7;
    ctx.beginPath();
    ctx.arc(cx, cy, r, rotation, rotation + Math.PI * 1.5);
    ctx.stroke();

    // Inner counter-rotating ring
    ctx.globalAlpha = 0.4;
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.6, -rotation, -rotation + Math.PI * 1.2);
    ctx.stroke();

    // Center point
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.8 + Math.sin(time / 400) * 0.2;
    ctx.beginPath();
    ctx.arc(cx, cy, 3, 0, Math.PI * 2);
    ctx.fill();

    // Packet stream particles
    ctx.globalAlpha = 0.5;
    for (let i = 0; i < 3; i++) {
      const t = (time / 800 + i * 0.33) % 1;
      const px = cx + r * t * 1.2;
      const py = cy + Math.sin(t * Math.PI * 2 + i) * 3;
      ctx.beginPath();
      ctx.arc(px, py, 1.5 * (1 - t), 0, Math.PI * 2);
      ctx.fill();
    }

    // SPAWN label
    ctx.font = canvasFont("fieldLabel");
    ctx.textAlign = "center";
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.6;
    ctx.fillText("SPAWN", cx, cy + size * 0.38);
    ctx.globalAlpha = 1;
  }

  /** Animated data-flow dashes along path cells. */
  private drawDataFlow(): void {
    if (this.pathCellsDirty) {
      this.pathCells = [];
      const size = this.size;
      for (let row = 0; row < this.grid.height; row++) {
        for (let col = 0; col < this.grid.width; col++) {
          const cell = this.grid.getCell(col, row);
          if (
            cell?.type === CellType.PATH_ENTRY ||
            cell?.type === CellType.PATH_EXIT
          ) {
            this.pathCells.push({
              x: col * size + size / 2,
              y: row * size + size / 2,
            });
          }
        }
      }
      this.pathCellsDirty = false;
    }

    if (this.pathCells.length === 0) return;

    const ctx = this.ctx;
    const size = this.size;
    const now = performance.now();
    const t = now * 0.002;
    const epoch = getEpochColors(this.wave);

    // S126: the data-flow stutters off-color for a beat (~0.3 s every 2.4 s) —
    // her signature riding the stream. Zero-mix in Act I; the boss path color
    // keeps priority (gameplay signal beats theming).
    let flowColor = epoch.primary;
    if (this.corruption > 0) {
      const stutter = (now / 1000) % 2.4 < 0.3 ? 0.35 : 0;
      flowColor = mixHex(
        epoch.primary,
        ECHELON.SIGNAL,
        this.corruption * (0.25 + stutter),
      );
    }

    // BL-232 — per-wave "GO" surge: flare the conduits bright + thicker, then
    // settle (quadratic decay over WAVE_SURGE_MS). Motion-gated → no flare under
    // reduced motion / load; a single ~0.6 s ramp-down, sub-strobe.
    const surgeRaw = Math.max(0, (this.waveSurgeUntil_ - now) / WAVE_SURGE_MS);
    const surge = surgeRaw * surgeRaw * arenaMotionScale();
    const baseAlpha = this.bossPathColor ? 1 : 0.12;
    ctx.strokeStyle = this.bossPathColor
      ? this.bossPathColor.glowColor(0.12)
      : flowColor;
    ctx.globalAlpha = Math.min(1, baseAlpha + surge * 0.7);
    ctx.lineWidth = 1 + surge * 1.6;
    ctx.setLineDash([4, 8]);
    ctx.lineDashOffset = -t * 10;

    for (const pc of this.pathCells) {
      ctx.beginPath();
      ctx.moveTo(pc.x - size / 2, pc.y);
      ctx.lineTo(pc.x + size / 2, pc.y);
      ctx.stroke();
    }

    ctx.setLineDash([]);
    ctx.globalAlpha = 1;
    ctx.lineDashOffset = 0;
  }

  private getTileColor(type: CellType): string {
    switch (type) {
      case CellType.TOWER:
        return COLORS.tileTower;
      case CellType.PATH_ENTRY:
        return COLORS.tileSpawn;
      case CellType.PATH_EXIT:
        return COLORS.tileExit;
      case CellType.BLOCKER:
        return "#1a0a0a";
      default:
        return COLORS.tileEmpty;
    }
  }

  /** CR-32 / BL-233: corrupted hex tile for blockers — a LEGIBLE "solid blocked
   *  zone" read. The old pastel-rose fill at α0.08 on the near-black board was
   *  almost invisible (owner: "kann man manchmal noch so gut erkennen"). This
   *  keeps the CR-32 hex-noise + dashed identity but adds a solid base fill,
   *  diagonal hazard stripes, a bright object border + corner brackets, and a
   *  soft outer glow — so the tile pops off the board at any breathing phase.
   *  The ~0.5 Hz breathing is sub-strobe and the legibility NEVER depends on it
   *  (every base alpha stays strong at the trough). Render-only; BL-125's
   *  strokeRect+setLineDash+fillText cover signature (and no `arc`) preserved. */
  private drawBlockers(time: number): void {
    const ctx = this.ctx;
    const size = this.size;
    for (let row = 0; row < this.grid.height; row++) {
      for (let col = 0; col < this.grid.width; col++) {
        const cell = this.grid.getCell(col, row);
        if (cell?.type !== CellType.BLOCKER) continue;
        const cx = col * size + size / 2;
        const cy = row * size + size / 2;
        const half = size * 0.42;
        const x0 = cx - half;
        const y0 = cy - half;
        const span = half * 2;
        // Slow (~0.5 Hz, sub-strobe) corruption breath — flavour only; the base
        // alphas below all stay clearly visible at the trough.
        const glitch = Math.sin(time / 300 + col * 7 + row * 13) * 0.12;

        // Soft outer glow — lifts the tile off the dark board.
        ctx.strokeStyle = CORE_COLORS.MALWARE_RED;
        ctx.lineWidth = 4;
        ctx.globalAlpha = 0.12 + glitch * 0.3;
        ctx.strokeRect(x0 - 1, y0 - 1, span + 2, span + 2);

        // Solid base fill — reads as an OCCUPIED, impassable tile (was α0.08).
        ctx.fillStyle = CORE_COLORS.MALWARE_RED;
        ctx.globalAlpha = 0.24 + glitch * 0.5;
        ctx.fillRect(x0, y0, span, span);

        // Diagonal hazard stripes — the unmistakable "blocked zone" read.
        ctx.save();
        ctx.beginPath();
        ctx.rect(x0, y0, span, span);
        ctx.clip();
        ctx.strokeStyle = CORE_COLORS.ORACLE_RED;
        ctx.lineWidth = 2;
        ctx.globalAlpha = 0.26 + glitch * 0.4;
        ctx.beginPath();
        const step = Math.max(7, size * 0.16);
        for (let d = -span; d <= span; d += step) {
          ctx.moveTo(x0 + d, y0);
          ctx.lineTo(x0 + d + span, y0 + span);
        }
        ctx.stroke();
        ctx.restore();

        // Hex noise — F11: deterministic per-cell + time-seeded (CR-32 identity).
        const noiseSeed = (col * 31 + row * 17 + Math.floor(time / 200)) | 0;
        ctx.font = canvasFont("fieldNoise");
        ctx.fillStyle = CORE_COLORS.MALWARE_RED;
        ctx.globalAlpha = 0.35 + ((noiseSeed & 0x0f) / 0xff) * 0.4;
        ctx.textAlign = "center";
        const hex = ((noiseSeed * 1103515245 + 12345) & 0xff)
          .toString(16)
          .toUpperCase()
          .padStart(2, "0");
        ctx.fillText(hex, cx, cy + 2);

        // Bright solid object border — the hard edge of the obstacle.
        ctx.strokeStyle = CORE_COLORS.ORACLE_RED;
        ctx.lineWidth = 2;
        ctx.globalAlpha = 0.6 + glitch * 0.3;
        ctx.strokeRect(x0, y0, span, span);

        // Dashed inner border — keep BL-125's cover-affordance signature.
        ctx.strokeStyle = CORE_COLORS.MALWARE_RED;
        ctx.lineWidth = 1;
        ctx.globalAlpha = 0.5;
        ctx.setLineDash([4, 3]);
        ctx.strokeRect(x0 + 3, y0 + 3, span - 6, span - 6);
        ctx.setLineDash([]);

        // Corner brackets — premium "locked tile" chrome.
        ctx.strokeStyle = CORE_COLORS.ORACLE_RED;
        ctx.lineWidth = 2;
        ctx.globalAlpha = 0.85;
        const ct = half * 0.5;
        ctx.beginPath();
        ctx.moveTo(x0, y0 + ct);
        ctx.lineTo(x0, y0);
        ctx.lineTo(x0 + ct, y0);
        ctx.moveTo(x0 + span - ct, y0);
        ctx.lineTo(x0 + span, y0);
        ctx.lineTo(x0 + span, y0 + ct);
        ctx.moveTo(x0 + span, y0 + span - ct);
        ctx.lineTo(x0 + span, y0 + span);
        ctx.lineTo(x0 + span - ct, y0 + span);
        ctx.moveTo(x0 + ct, y0 + span);
        ctx.lineTo(x0, y0 + span);
        ctx.lineTo(x0, y0 + span - ct);
        ctx.stroke();

        ctx.globalAlpha = 1;
      }
    }
  }

  /** Trigger the threat pulse animation on dormant spawns */
  triggerThreatPulse(durationMs: number): void {
    this.threatPulseUntil_ = Date.now() + durationMs;
  }

  /** BL-232 — trigger the per-wave "GO" surge: the path data-flow flares bright
   *  (and thicker) then settles over `durationMs`, reading as the conduits going
   *  live when a wave starts. Render-only; the flare self-gates reduced motion
   *  (arenaMotionScale) in {@link drawDataFlow}. */
  triggerWaveSurge(durationMs: number = WAVE_SURGE_MS): void {
    this.waveSurgeUntil_ = Date.now() + durationMs;
  }

  /** Check if threat pulse is currently active (for testing) */
  get threatPulseActive(): boolean {
    return Date.now() < this.threatPulseUntil_;
  }

  /** Trigger a brief rejection flash on a cell */
  showRejectFlash(col: number, row: number, reason: string): void {
    const color = reason === "not_affordable" ? CORE_COLORS.AMBER_WARN : CORE_COLORS.ORACLE_RED;
    this.rejectFlash = { col, row, timer: 250, color };
  }

  /** Update rejection flash timer (call from render pipeline update) */
  updateRejectFlash(dt: number): void {
    if (this.rejectFlash) {
      this.rejectFlash.timer -= dt;
      if (this.rejectFlash.timer <= 0) this.rejectFlash = null;
    }
  }

  /** Render the rejection flash overlay */
  renderRejectFlash(): void {
    if (!this.rejectFlash) return;
    const { col, row, timer, color } = this.rejectFlash;
    const ctx = this.ctx;
    const size = this.size;
    const alpha = Math.min(timer / 250, 1) * 0.4;
    ctx.fillStyle = color;
    ctx.globalAlpha = alpha;
    ctx.fillRect(col * size, row * size, size, size);
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.globalAlpha = alpha * 1.5;
    ctx.strokeRect(col * size + 1, row * size + 1, size - 2, size - 2);
    ctx.globalAlpha = 1;
  }
}
